import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { OnboardingShell } from '@/onboarding/OnboardingShell'
import { bookmarkStep } from '@/api/onboarding'
import { LegalFooter } from '@/components/LegalFooter'
import { emitPollEvent } from '@/api/pollEvents'

/**
 * Magic-link перехват (`?magic=<token>`) делается в `AuthContext:45` —
 * там же F-side polls-flow override (contract-doc §4). Сюда WelcomeIntro
 * попадает уже без `?magic=`, только с `?ref=<pollId>` для n=0-alias
 * `/onboarding/channel?ref=`.
 */
export function WelcomeIntroPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const refPollId = params.get('ref')

  useEffect(() => {
    if (refPollId) {
      emitPollEvent('poll_onboarding_opened', {
        pollId: refPollId,
        meta: { source: 'lk', entryPath: window.location.pathname, ref: refPollId },
      })
    }
  }, [refPollId])

  function handleStart() {
    bookmarkStep('overview')
    // ref sticky в URL — таскаем через onboarding-цепочку, чтоб step_completed
    // и channel_connected знали свой anchor pollId.
    const qs = refPollId ? `?ref=${encodeURIComponent(refPollId)}` : ''
    navigate(`/onboarding/overview${qs}`)
  }

  return (
    <OnboardingShell step="welcome-intro" variant="minimal">
      <div className="hero-grid">
        <div className="hero-art">
          <svg viewBox="0 0 540 360" className="hero-chart" preserveAspectRatio="xMidYMid meet">
            <defs>
              <linearGradient id="hero-grad" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#0080FF" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#0080FF" stopOpacity="0" />
              </linearGradient>
              <pattern id="hero-grid-pat" width="60" height="60" patternUnits="userSpaceOnUse">
                <path d="M60 0L0 0 0 60" fill="none" stroke="rgba(143,158,176,0.18)" strokeWidth="1" strokeDasharray="2 4" />
              </pattern>
            </defs>
            <rect width="540" height="360" fill="url(#hero-grid-pat)" />
            <path
              d="M0,300 C60,290 100,250 160,240 C220,230 250,260 300,210 C350,160 400,140 450,90 C480,60 510,40 540,30 L540,360 L0,360 Z"
              fill="url(#hero-grad)"
            />
            <path
              d="M0,300 C60,290 100,250 160,240 C220,230 250,260 300,210 C350,160 400,140 450,90 C480,60 510,40 540,30"
              fill="none"
              stroke="#0080FF"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
            <circle cx="160" cy="240" r="4" fill="white" stroke="#0080FF" strokeWidth="2" />
            <circle cx="300" cy="210" r="4" fill="white" stroke="#0080FF" strokeWidth="2" />
            <circle cx="450" cy="90" r="6" fill="#0080FF" />
            <g transform="translate(110, 200)">
              <rect x="0" y="0" width="100" height="38" rx="10" fill="white" stroke="rgba(143,158,176,0.24)" />
              <text x="14" y="16" fontFamily="Inter, sans-serif" fontSize="9" fontWeight="700" fill="#8F9EB0" letterSpacing="0.06em">
                КАНАЛ ПОДКЛЮЧЁН
              </text>
              <text x="14" y="29" fontFamily="Inter, sans-serif" fontSize="11" fontWeight="700" fill="#26282D">
                +312 за сутки
              </text>
            </g>
            <g transform="translate(360, 30)">
              <rect x="0" y="0" width="116" height="46" rx="10" fill="#26282D" />
              <text x="14" y="18" fontFamily="Inter, sans-serif" fontSize="9" fontWeight="700" fill="rgba(255,213,80,0.9)" letterSpacing="0.06em">
                К ВЕЧЕРУ
              </text>
              <text x="14" y="33" fontFamily="Inter, sans-serif" fontSize="13" fontWeight="800" fill="white">
                5 132 → 5 444
              </text>
            </g>
          </svg>
        </div>
        <div className="hero-text">
          <p className="stage-eyebrow">
            <span className="stage-eyebrow-pill">Кабинет TeleGraphyx</span>
          </p>
          <h1 className="hero-title">
            Канал подключён, <em>реклама запущена</em>&nbsp;— за&nbsp;10&nbsp;минут.
          </h1>
          <p className="hero-sub">
            Подключим ваш канал в&nbsp;MAX, дадим аналитику, лендинги, умные кнопки и&nbsp;запустим первую кампанию&nbsp;Я.Директа. <b>Готовы?</b>
          </p>

          <div className="hero-cta">
            <button className="hero-cta-primary" onClick={handleStart}>
              Начать
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M13 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          <ul className="hero-bullets">
            <li>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5" />
              </svg>
              7 дней Профи за 1&nbsp;₽
            </li>
            <li>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5" />
              </svg>
              Без карты на старте
            </li>
            <li>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5" />
              </svg>
              Отмена в один клик
            </li>
          </ul>
        </div>
      </div>

      <LegalFooter />

      <style>{`
        .hero-grid {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
          gap: 64px;
          align-items: center;
          max-width: 1200px;
          margin: 32px auto 0;
        }
        @media (max-width: 1024px) {
          .hero-grid { grid-template-columns: 1fr; gap: 32px; }
        }
        .hero-art {
          width: 100%;
          max-width: 560px;
        }
        .hero-chart { width: 100%; height: auto; }
        .hero-title {
          font-weight: 800;
          font-size: 52px;
          line-height: 1.05;
          letter-spacing: -0.025em;
          color: var(--tg-ink, #111);
          margin: 0 0 16px;
        }
        .hero-title em {
          font-family: 'Instrument Serif', serif;
          font-style: italic;
          font-weight: 400;
          color: var(--tg-blue, #0080ff);
        }
        .hero-sub {
          font-size: 18px;
          line-height: 1.55;
          color: var(--tg-ink-2, #333);
          margin: 0 0 32px;
        }
        .hero-sub b { color: var(--tg-ink, #111); font-weight: 700; }
        .hero-cta {
          display: flex;
          gap: 12px;
          align-items: center;
          margin-bottom: 24px;
          flex-wrap: wrap;
        }
        .hero-cta-primary {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 16px 28px;
          background: var(--tg-blue, #0080ff);
          color: white;
          border: 0;
          border-radius: 12px;
          font-family: inherit;
          font-weight: 700;
          font-size: 17px;
          cursor: pointer;
          box-shadow: 0 8px 24px -8px rgba(0,128,255,0.5);
        }
        .hero-cta-primary:hover { background: var(--tg-blue-hover, #0066cc); }
        .hero-cta-primary svg { width: 18px; height: 18px; }
        .hero-cta-skip {
          padding: 16px 20px;
          background: transparent;
          color: var(--tg-ink-2, #333);
          border: 0;
          border-radius: 12px;
          font-family: inherit;
          font-weight: 600;
          font-size: 15px;
          cursor: pointer;
        }
        .hero-cta-skip:hover { background: var(--tg-surface-2, #f5f5f9); }
        .hero-bullets {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          gap: 22px;
          flex-wrap: wrap;
          font-size: 13px;
          color: var(--tg-ink-2, #333);
          font-weight: 500;
        }
        .hero-bullets li {
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .hero-bullets svg {
          width: 14px;
          height: 14px;
          color: var(--tg-up, #00a860);
        }
      `}</style>
    </OnboardingShell>
  )
}
